import React, { useEffect, useState, useCallback, useRef } from "react"
import { connect } from "getstream"

const INTEREST_TAGS = ["funnels", "courses", "templates", "ai-tools", "coaching"]

const ForYouFeed = ({ feedGroup = "timeline", feedId, apiKey, appId, token, userId, interests = [] }) => {
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [nextId, setNextId] = useState(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const observerRef = useRef(null)
  const sentinelRef = useRef(null)

  const fetchActivities = useCallback(async (idLt = null) => {
    if (!apiKey || !appId || !token || !feedId) {
      setError("Missing required configuration")
      setLoading(false)
      return
    }

    try {
      if (idLt) setLoadingMore(true)
      const client = connect(apiKey, null, appId)
      const feed = client.feed(feedGroup, feedId, token)
      const params = { limit: 20 }
      if (idLt) params.id_lt = idLt

      const response = await feed.get(params)
      const results = response.results || []

      // Client-side scoring: interest_score * popularity * time_decay
      const scored = results.map(a => {
        const tags = a.interest_tags || []
        const interestScore = interests.length > 0
          ? tags.filter(t => interests.includes(t)).length / Math.max(interests.length, 1)
          : 0.5
        const popularity = (a.reaction_counts?.like || 0) + (a.reaction_counts?.comment || 0) * 2
        const ageHours = (Date.now() - new Date(a.time)) / 3600000
        const timeDecay = Math.exp(-0.05 * ageHours)
        return { ...a, _score: (interestScore + 0.5) * (popularity + 1) * timeDecay }
      }).sort((a, b) => b._score - a._score)

      if (idLt) {
        setActivities(prev => [...prev, ...scored])
      } else {
        setActivities(scored)
      }

      setHasMore(results.length === 20)
      if (results.length > 0) setNextId(results[results.length - 1].id)
      setLoading(false)
      setLoadingMore(false)
    } catch (err) {
      console.error("ForYouFeed: Failed to fetch:", err)
      setError(`Failed to load feed: ${err.message}`)
      setLoading(false)
      setLoadingMore(false)
    }
  }, [apiKey, appId, token, feedId, feedGroup, interests])

  useEffect(() => { fetchActivities() }, [fetchActivities])

  // Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && nextId) {
        fetchActivities(nextId)
      }
    }, { threshold: 0.1 })
    observer.observe(sentinelRef.current)
    observerRef.current = observer
    return () => observer.disconnect()
  }, [hasMore, loadingMore, nextId, fetchActivities])

  const formatTime = (timestamp) => {
    const diff = Date.now() - new Date(timestamp)
    const mins = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    if (mins < 1) return "just now"
    if (mins < 60) return `${mins}m ago`
    if (hours < 24) return `${hours}h ago`
    return `${days}d ago`
  }

  const renderActivity = (activity) => {
    const actorName = activity.actor?.name || activity.actor || "Someone"
    const tags = activity.interest_tags || []

    return (
      <div key={activity.id} className="cf-foryou-item">
        <div className="cf-foryou-item__avatar">
          {(typeof actorName === "string" ? actorName[0] : "?").toUpperCase()}
        </div>
        <div className="cf-foryou-item__body">
          <div className="cf-foryou-item__header">
            <strong>{actorName}</strong>
            <span className="cf-foryou-item__time">{formatTime(activity.time)}</span>
          </div>
          <div className="cf-foryou-item__content">
            {activity.message || activity.text || activity.object?.text || activity.verb || ""}
          </div>
          {tags.length > 0 && (
            <div className="cf-foryou-item__tags">
              {tags.map(t => <span key={t} className="cf-foryou-tag">{t}</span>)}
            </div>
          )}
          {activity.reaction_counts && (
            <div className="cf-foryou-item__reactions">
              {activity.reaction_counts.like > 0 && <span>&#128077; {activity.reaction_counts.like}</span>}
              {activity.reaction_counts.fire > 0 && <span>&#128293; {activity.reaction_counts.fire}</span>}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (error) return <div className="cf-feed-error"><p>{error}</p></div>

  if (loading) {
    return (
      <div className="cf-feed-loading">
        <div className="cf-feed-loading__spinner" />
        <p>Loading your personalized feed...</p>
      </div>
    )
  }

  return (
    <div className="cf-foryou-feed">
      {activities.length === 0 ? (
        <div className="cf-feed-empty">
          <p>Nothing here yet</p>
          <p className="cf-feed-empty__hint">Follow stores and creators to fill your For You feed</p>
        </div>
      ) : (
        <div className="cf-foryou-list">
          {activities.map(renderActivity)}
          {hasMore && <div ref={sentinelRef} className="cf-foryou-sentinel" />}
          {loadingMore && (
            <div className="cf-feed-loading cf-feed-loading--inline">
              <div className="cf-feed-loading__spinner" />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ForYouFeed
